import type { OnDestroy } from '@angular/core';
import { Injectable } from '@angular/core';
import type { Subscription } from 'rxjs';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { AnalyticsService } from '@shared/services/analytics/analytics.service';
import {
  applyAxisDeadzone,
  applyAxisExpo,
  ButtonDefWithValue,
  DEFAULT_AXIS_DEADZONE,
  DEFAULT_AXIS_EXPO,
  defaultAxisDeadzones,
  defaultAxisExpos,
  Gamepad,
  isAnalogButtonType,
  isAxisButtonType,
} from '../../models';

enum GamepadActiveStateName {
  LOSE_FOCUS = 'loseFocus',
  GAIN_FOCUS = 'gainFocus',
  USER_IDLE_UNFOCUSED = 'userIdleUnFocused',
  USER_IDLE_FOCUSED = 'userIdleFocused',
  ACTIVE = 'active',
}

export enum GamepadNotificationName {
  LOSE_FOCUS_WHEN_GAMEPAD_ENABLED = 'loseFocusWhenGamepadEnabled',
  GAIN_FOCUS_WHEN_GAMEPAD_CANBE_RESUMED = 'gainFocusWhenGamepadCanbeResumed',
  IDLE_TIMEOUT_WHEN_GAMEPAD_ENABLED = 'idleTimeoutWhenGamepadEnabled',
  IDLE_TIMEOUT_WHEN_GAMEPAD_CANBE_RESUMED = 'idleTimeoutWhenGamepadCanbeResumed',
}

/**
 * Activity threshold for non-axis inputs (triggers/buttons) — the minimum value that counts as a
 * deliberate press for idle detection. Stick axes are deadzoned per-profile elsewhere; this is not
 * an axis deadband. Kept at the default axis deadzone magnitude for consistency.
 */
export const triggerActivityThreshold = DEFAULT_AXIS_DEADZONE;

@Injectable({
  providedIn: 'root',
})

// TODO: The GamepadService and GamepadConnectionService has duplicate code and should be refactor to one class
// https://app.clubhouse.io/rocos/story/7424/refactor-gamepadservice-and-gamepadconnectionservice
export class GamepadConnectionService implements OnDestroy {
  public gamepadUpdated$ = new BehaviorSubject<Gamepad[]>([]);
  public gamepadPressed$ = new Subject<Gamepad>();
  public gamepadNotification$ = new Subject<GamepadNotificationName>();
  public gamepadEnabled$ = new BehaviorSubject<boolean>(false);

  ticker: NodeJS.Timeout;
  threshold: number = triggerActivityThreshold;
  maxIdleTimeToAllowResume: number = 0.5 * 60 * 1000; // 30 seconds
  maxIdleTimeFocused: number = 5 * 60 * 1000; // 5 minutes
  lastGamepadInputTimestamp;
  lastHasFocus: boolean;
  lastIdleState: string;
  lastFocusTimestamp: number = Date.now();
  isIdle: boolean;
  lastUserEnableTimestamp: number;
  lastAutoDisableTimestamp: number;
  lastUserDisableTimestamp: number;
  keepAlive: boolean;

  private gamepadActiveState$ = new Subject<GamepadActiveStateName>();
  private gamepadEnabled: boolean = false;
  private lastGamepads = {};
  private gamepads: Gamepad[] = [];
  private subs: Subscription[] = [];
  private frameIntervalDelayMs = 100;
  private frameRefreshInterval;

  private gamepadPressInfo: {
    [gamepadId: string]: {
      isNoPressed: boolean;
    };
  } = {};

  private pressedButtons: ButtonDefWithValue[] = [];

  /** Profile-level per-axis deadzone map, keyed by stick-axis ButtonType. */
  private axisDeadzones: { [buttonType: number]: number } = defaultAxisDeadzones();

  /** Profile-level per-axis expo (response-curve) map, keyed by stick-axis ButtonType. */
  private axisExpos: { [buttonType: number]: number } = defaultAxisExpos();

  constructor(private analyticsService: AnalyticsService) {
    this.listenGamepadConnections();
    this.watchGamepadActiveState();
    this.listenGamepadActiveState();

    this.gamepadEnabled$.asObservable().subscribe((x) => {
      this.gamepadEnabled = x;
    });
  }

  // TODO: there are no two gamepad components can be on the same page at the moment due to the singleton injection
  // Refactor the whole gamepad service to non-singleton  injection
  // each gamepad compoment would have its own copy of gamepad servcie
  public resetBehaviour() {
    this.keepAlive = false;
  }

  public setKeepAlive(alive: boolean): void {
    this.keepAlive = alive;
  }

  public checkGamepadActiveState() {
    const newIdleState = this.newIdleState();
    // we only notify the subscribers when the idlestate changed
    if (newIdleState) {
      this.gamepadActiveState$.next(newIdleState);
    } else if (this.documentFocusStateChanged()) {
      if (document.hasFocus()) {
        this.gamepadActiveState$.next(GamepadActiveStateName.GAIN_FOCUS);
      } else {
        this.gamepadActiveState$.next(GamepadActiveStateName.LOSE_FOCUS);
      }
    }
  }

  public listenGamepadActiveState() {
    this.gamepadActiveState$.subscribe((state) => {
      switch (state) {
        case GamepadActiveStateName.USER_IDLE_FOCUSED:
          if (this.gamepadEnabled) {
            this.isIdle = true;
            this.toggleGamepad(false, false);
            this.gamepadNotification$.next(GamepadNotificationName.IDLE_TIMEOUT_WHEN_GAMEPAD_ENABLED);
          }
          break;
        case GamepadActiveStateName.USER_IDLE_UNFOCUSED:
          if (this.canBeResumed()) {
            this.isIdle = true;
            this.toggleGamepad(false, false);
            this.gamepadNotification$.next(GamepadNotificationName.IDLE_TIMEOUT_WHEN_GAMEPAD_CANBE_RESUMED);
          }
          break;
        case GamepadActiveStateName.LOSE_FOCUS:
          if (this.gamepadEnabled) {
            this.toggleGamepad(false, false);
            this.gamepadNotification$.next(GamepadNotificationName.LOSE_FOCUS_WHEN_GAMEPAD_ENABLED);
          }
          break;
        case GamepadActiveStateName.GAIN_FOCUS:
          if (!this.gamepadEnabled && this.canBeResumed()) {
            this.toggleGamepad(true, false);
            this.gamepadNotification$.next(GamepadNotificationName.GAIN_FOCUS_WHEN_GAMEPAD_CANBE_RESUMED);
          }
          break;
      }
    });
  }

  canBeResumed() {
    if (!this.lastUserEnableTimestamp) {
      // The gamepad is never been enabled, cannot be resumed
      return false;
    }

    if (!this.lastAutoDisableTimestamp) {
      // The widget was not disalbed by ticker, return
      return false;
    }

    if (this.lastUserDisableTimestamp && this.lastAutoDisableTimestamp < this.lastUserDisableTimestamp) {
      // last disable was done by user, don't resume
      return false;
    }

    return !this.isIdle;
  }

  toggleGamepad(enable: boolean, isUser: boolean) {
    const now = Date.now();
    if (enable) {
      if (isUser) {
        this.lastUserEnableTimestamp = now;
      }

      this.resetLastGamepadInputTimestamp();
      this.isIdle = false;
    } else {
      if (isUser) {
        this.lastUserDisableTimestamp = now;
      } else {
        this.lastAutoDisableTimestamp = now;
      }
    }

    this.gamepadEnabled$.next(enable);
  }

  newIdleState() {
    // When keep alive, gamepad never be idle
    if (this.keepAlive) {
      return null;
    }

    const idleState = this.getIdleState();
    if (idleState !== this.lastIdleState) {
      this.lastIdleState = idleState;
      return idleState;
    }

    return null;
  }

  getIdleState(): GamepadActiveStateName {
    if (document.hasFocus()) {
      return this.isIdleFocused() ? GamepadActiveStateName.USER_IDLE_FOCUSED : null;
    } else {
      return this.isIdleUnFocused() ? GamepadActiveStateName.USER_IDLE_UNFOCUSED : null;
    }
  }

  public resetLastFocusTimestamp() {
    this.lastFocusTimestamp = Date.now();
  }

  public resetLastGamepadInputTimestamp() {
    this.lastGamepadInputTimestamp = Date.now();
  }

  ngOnDestroy() {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  /** Set the profile's per-axis deadzone map. Missing axes fall back to the default deadzone. */
  setAxisDeadzones(deadzones: { [buttonType: number]: number }) {
    this.axisDeadzones = { ...defaultAxisDeadzones(), ...(deadzones || {}) };
  }

  /** Set the profile's per-axis expo map. Missing axes fall back to the default expo. */
  setAxisExpos(expos: { [buttonType: number]: number }) {
    this.axisExpos = { ...defaultAxisExpos(), ...(expos || {}) };
  }

  /**
   * Shape every stick-axis value through its configured deadzone and expo curve so all downstream
   * consumers (indicators, action service, previews) see the same value. The deadzone rescales the
   * value smoothly from 0 at its edge to full deflection; the expo then softens the response near
   * centre. Values within the deadzone become exactly 0.
   *
   * Public so the parallel GamepadService (which reads the hardware independently) can share this
   * single shaping implementation and the same configured deadzones/expos.
   *
   * NOTE: mutates `gamepad.pressedValues` in place. Callers must invoke it on a freshly-built
   * gamepad (e.g. from `Gamepad.fromGamepadModel`) before snapshotting or emitting its values.
   */
  applyAxisCurves(gamepad: Gamepad) {
    gamepad.pressedValues?.forEach((pressedValue) => {
      if (isAxisButtonType(pressedValue.type)) {
        const deadzone = this.axisDeadzones[pressedValue.type] ?? DEFAULT_AXIS_DEADZONE;
        const expo = this.axisExpos[pressedValue.type] ?? DEFAULT_AXIS_EXPO;

        const deadzoned = applyAxisDeadzone(pressedValue.value, deadzone);
        pressedValue.value = applyAxisExpo(deadzoned, expo);
      }
    });
  }

  private watchGamepadActiveState() {
    this.ticker = setInterval(() => {
      this.checkGamepadActiveState();
    }, 100);
  }

  private documentFocusStateChanged() {
    const currentHasFocus = document.hasFocus();
    if (currentHasFocus) {
      this.lastFocusTimestamp = Date.now();
    }

    if (this.lastHasFocus !== currentHasFocus) {
      this.lastHasFocus = currentHasFocus;
      return true;
    }

    this.lastHasFocus = currentHasFocus;
    return undefined;
  }

  private isIdleUnFocused() {
    const idlePeroid = this.getUnFocusPeroid();
    return idlePeroid > this.maxIdleTimeToAllowResume;
  }

  private isIdleFocused() {
    const idlePeroid = this.getIdlePeroid();
    return idlePeroid > this.maxIdleTimeFocused;
  }

  private getIdlePeroid() {
    const now = Date.now();
    return now - this.lastGamepadInputTimestamp;
  }

  private getUnFocusPeroid() {
    const now = Date.now();
    return now - this.lastFocusTimestamp;
  }

  private setLastGamepadInputTimestamp(gamepad) {
    for (const pressValue of gamepad.pressedValues) {
      if (this.isDeliberateInput(pressValue)) {
        this.lastGamepadInputTimestamp = Date.now();
        break;
      }
    }
  }

  /** Whether an input should hold off the idle timeout. */
  private isDeliberateInput(pressValue: ButtonDefWithValue): boolean {
    // Deadzoned and rescaled upstream, so any deflection left is real — including a negative one.
    if (isAxisButtonType(pressValue.type)) {
      return pressValue.value !== 0;
    }

    // Triggers reach us whenever deflected, so a worn one resting above the threshold would reset the
    // timer at frame rate and the timeout would never fire. `pressed` separates a pull from drift.
    if (isAnalogButtonType(pressValue.type)) {
      return pressValue.value > this.threshold && pressValue.pressed;
    }

    return pressValue.value > this.threshold;
  }

  private listenGamepadConnections() {
    const gamepadConnectedSub = fromEvent<GamepadEvent>(window, 'gamepadconnected').subscribe((event) => {
      // Run the gamepad reload first; logging/analytics is best-effort and must not delay input handling.
      this.reloadGamepads();
      // eslint-disable-next-line no-console -- debug-level diagnostic, opt-in via DevTools "Verbose"
      console.debug(
        `[gamepad] connected: "${event.gamepad?.id}" ` +
          `(index=${event.gamepad?.index}, mapping=${event.gamepad?.mapping})`,
      );
      this.analyticsService.trackGamepadEvent('connected', {
        gamepadId: event.gamepad?.id,
        index: event.gamepad?.index,
        mapping: event.gamepad?.mapping,
      });
    });

    this.subs.push(gamepadConnectedSub);

    const gamepadDisconnectedSub = fromEvent<GamepadEvent>(window, 'gamepaddisconnected').subscribe((event) => {
      this.reloadGamepads();
      // eslint-disable-next-line no-console -- debug-level diagnostic, opt-in via DevTools "Verbose"
      console.debug(`[gamepad] disconnected: "${event.gamepad?.id}" (index=${event.gamepad?.index})`);
      this.analyticsService.trackGamepadEvent('disconnected', {
        gamepadId: event.gamepad?.id,
        index: event.gamepad?.index,
      });
    });

    this.subs.push(gamepadDisconnectedSub);

    // Reload gamepads by default.
    this.reloadGamepads();
  }

  private reloadGamepads(createRefreshInterval = true) {
    const browserGamePads = navigator.getGamepads();
    const gamepads = [];

    for (const gamepad of browserGamePads) {
      if (gamepad?.id != null) {
        Gamepad.fromGamepadModel(gamepad);
        gamepads.push(Gamepad.fromGamepadModel(gamepad));
      }
    }

    this.gamepads = gamepads;

    // Shape stick axes through their configured deadzone + expo up front — before any stream is
    // emitted — so the updated stream, release detection, the cached last-state and every
    // downstream subscriber all see the same values.
    this.gamepads.forEach((gamepad) => this.applyAxisCurves(gamepad));

    if (createRefreshInterval) {
      this.gamepadUpdated$.next(this.gamepads);
    }

    if (this.gamepads?.length > 0) {
      this.gamepads.forEach((gamepad) => {
        // Initialize gamepadPressInfo if it doesn't exist.
        if (!this.gamepadPressInfo[gamepad.id]) {
          this.gamepadPressInfo[gamepad.id] = {
            isNoPressed: false,
          };
        }

        // detect gamepad release event
        // when a key is released from the gamepad, the key will not be returned from the gamepad
        // we detect a key released by checking the last state of the key value
        if (this.lastGamepads[gamepad.id]?.pressedValues) {
          this.lastGamepads[gamepad.id].pressedValues.forEach((x) => {
            if (x.value === 0) {
              return;
            }
            const foundKeys = gamepad.pressedValues.filter((y) => {
              return y.type === x.type;
            });
            if (!foundKeys || foundKeys.length === 0) {
              // Axis control does not require release event.

              // The button is missing from the gamepad new frame, which means the button is released
              // we treat a button release as a button pressed with value 0
              const button = new ButtonDefWithValue(x.type, x.text, 0);
              gamepad.pressedValues.push(button);
            }
          });
        }

        // record the last gamepad state
        this.lastGamepads[gamepad.id] = gamepad;

        if (gamepad.pressedValues?.length > 0) {
          this.gamepadPressed$.next(gamepad);
          this.pressedButtons = gamepad.pressedValues;
          this.setLastGamepadInputTimestamp(gamepad);

          // We got pressed buttons
          this.gamepadPressInfo[gamepad.id].isNoPressed = false;
        } else {
          if (this.gamepadPressInfo[gamepad.id]) {
            const pressedInfo = this.gamepadPressInfo[gamepad.id];
            if (!pressedInfo.isNoPressed) {
              this.gamepadPressed$.next(gamepad);
              pressedInfo.isNoPressed = true;
            }
          }
        }
      });
    } else {
      // all gamepads are removed, we will disable the gamepad
      this.toggleGamepad(false, true);
    }

    if (createRefreshInterval && this.gamepads) {
      this.updateFrameRefreshInterval();
    }
  }

  private updateFrameRefreshInterval() {
    if (this.gamepads?.length > 0) {
      clearInterval(this.frameRefreshInterval);
      this.frameRefreshInterval = setInterval(() => {
        this.reloadGamepads(false);
      }, this.frameIntervalDelayMs);
    } else {
      clearInterval(this.frameRefreshInterval);
    }
  }
}
